import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Grid, Divider } from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import LineGraph from '../common/LineGraph/LineGraph';
import EnhancedTable from '../common/EnhancedTable/EnhancedTable';
import GenericStatsForm from '../common/GenericStatsForm/GenericStatsForm';
import routeConstants from '../../Routes/routeConstants';
import trackingUtils from '../../utils/trackingUtils';
import GridCard from '../common/GridCard/GridCard';
import HugeDataTitle from '../common/HugeDataTitle/HugeDataTitle';
import DoughnutInGridCardWithHeader from '../common/DoughnutInGridCardWithHeader/DoughnutInGridCardWithHeader';
import graphKeysPropTypes from '../../proptypes/graphKeysPropTypes';
import PageHeader from '../common/PageHeader/PageHeader';
import NoResultMessage from '../common/NoResultMessage/NoResultMessage';
import SubHeader from '../common/SubHeader/SubHeader';
import translationPropType from '../../proptypes/translationPropType';
import genericFormStatePropTypes from '../../proptypes/genericFormStatePropTypes';

const styles = theme => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(2),
        maxWidth: '100%',
    },
});

const LeadsStatsWrapper = ({
    classes,
    width,
    graphData,
    sourceData,
    pageData,
    customerData,
    leadOriginData,
    handleGroupBy,
    groupBy,
    graphKeys,
    preSelectedGraphKeys,
    handleSelectGraphKeys,
    tableData,
    totalCount,
    totalCountString,
    isAnalyzeUser,
    translation,
    ...formProps
}) => {
    useEffect(() => {
        trackingUtils.trackPageview(routeConstants.LEADS_STATS);
    }, []);

    useEffect(() => {
        document.title = translation.LEADS_ANALYSIS_PAGE_TITLE;
    }, [translation]);

    const noResult = () => totalCount === 0;

    return (
        <div className={classes.root}>
            { !isAnalyzeUser && (<Redirect to="/" />)}

            <Grid container spacing={2}>
                <GridCard xs={12} lg={8}>
                    <PageHeader header={translation.LEADS} />
                    <GenericStatsForm
                        searchActive={totalCountString !== null}
                        translation={translation}
                        {...formProps}
                    />
                </GridCard>
                <Grid item xs={12}>
                    <Divider />
                </Grid>
                {noResult() && (
                    <Grid item xs={12}>
                        <NoResultMessage translation={translation} />
                    </Grid>
                )}
                {!noResult() && totalCountString && (
                    <Grid item xs={12}>
                        <HugeDataTitle
                            data={totalCountString}
                            dataType={translation.LEADS}
                        />
                    </Grid>
                )
                }
                {!noResult() && graphData && (
                    <GridCard xs={12} xl={6}>
                        <SubHeader header={`${translation.LEADS_PER} ${translation[groupBy].toLowerCase()}`} />
                        {isWidthUp('sm', width) && (
                            <LineGraph
                                graphData={graphData}
                                graphKeys={graphKeys}
                                groupBy={groupBy}
                                handleGroupBy={handleGroupBy}
                                selectedGraphKeys={preSelectedGraphKeys}
                                translation={translation}
                                isAdvancedUser={isAnalyzeUser}
                                handleSelectGraphKeys={handleSelectGraphKeys}
                            />
                        )}
                    </GridCard>
                )
                }
                {!noResult() && tableData && (
                    <GridCard xs={12} xl={6}>
                        <EnhancedTable
                            tableTitle={translation.LEADS_PER_TYPE}
                            rows={tableData.dataRows}
                            cols={tableData.dataCols}
                            translation={translation}
                        />
                    </GridCard>
                )
                }
                {!noResult() && sourceData && (
                    <DoughnutInGridCardWithHeader
                        header={translation.SOURCE}
                        data={sourceData}
                    />
                )
                }
                {!noResult() && pageData && (
                    <DoughnutInGridCardWithHeader
                        header={translation.PAGE}
                        data={pageData}
                    />
                )
                }
                {!noResult() && customerData && (
                    <DoughnutInGridCardWithHeader
                        header={`${translation.SHARE} ${translation.CUSTOMER}`}
                        data={customerData}
                    />
                )
                }
                {!noResult() && leadOriginData && (
                    <DoughnutInGridCardWithHeader
                        header={translation.LEAD_ORIGIN}
                        data={leadOriginData}
                    />
                )
                }
            </Grid>
        </div>
    );
};

LeadsStatsWrapper.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    handleClear: PropTypes.func.isRequired,
    handleGroupBy: PropTypes.func.isRequired,
    handleUpdateFormField: PropTypes.func.isRequired,
    groupBy: PropTypes.string.isRequired,
    preSelectedGraphKeys: graphKeysPropTypes,
    handleSelectGraphKeys: PropTypes.func.isRequired,
    graphKeys: graphKeysPropTypes,
    handleSubmit: PropTypes.func.isRequired,
    formState: genericFormStatePropTypes.isRequired,
    graphData: PropTypes.objectOf(PropTypes.any),
    sourceData: PropTypes.objectOf(PropTypes.any),
    customerData: PropTypes.objectOf(PropTypes.any),
    pageData: PropTypes.objectOf(PropTypes.any),
    leadOriginData: PropTypes.objectOf(PropTypes.any),
    tableData: PropTypes.objectOf(PropTypes.any),
    width: PropTypes.string.isRequired,
    totalCount: PropTypes.number,
    totalCountString: PropTypes.string,
    isAnalyzeUser: PropTypes.bool.isRequired,
    userLocale: PropTypes.string.isRequired,
};

LeadsStatsWrapper.defaultProps = {
    graphData: null,
    sourceData: null,
    pageData: null,
    customerData: null,
    graphKeys: null,
    leadOriginData: null,
    preSelectedGraphKeys: null,
    tableData: null,
    totalCount: null,
    totalCountString: null,
};

export default withWidth()(withStyles(styles)(LeadsStatsWrapper));
